// Cognito User Pools admin client — browser-friendly wrapper over the
// AWS SDK's CognitoIdentityProviderClient.
//
// Maps the "Keycloak realm per project" model directly to "Cognito
// User Pool per project":
//   - Pool = realm
//   - App Client = realm's OIDC client (`{slug}-spa` — the only one)
//   - User Pool group (optional) = realm role
//
// Auth: caller supplies AWS credentials (typically a STS session) +
// region. All methods are idempotent — `ensure*` re-fetches and
// updates if present, creates if not.

import {
  AdminCreateUserCommand,
  AdminSetUserPasswordCommand,
  CognitoIdentityProviderClient,
  CreateGroupCommand,
  CreateUserPoolClientCommand,
  CreateUserPoolCommand,
  GetGroupCommand,
  ListUserPoolClientsCommand,
  ListUserPoolsCommand,
  TagResourceCommand,
  UpdateUserPoolClientCommand,
} from "https://cdn.jsdelivr.net/npm/@aws-sdk/client-cognito-identity-provider@3.1054.0/+esm";

export interface CognitoAuthConfig {
  region: string;
  /** Credentials resolver (called per request so STS refresh rolls through). */
  credentials: () => Promise<{
    accessKeyId: string;
    secretAccessKey: string;
    sessionToken?: string;
  }>;
}

export interface CognitoUserPoolSpec {
  /** Project slug — becomes the User Pool name. */
  slug: string;
  /** Origins the app may be loaded from (callback URLs + allowed origins). */
  hosts: string[];
  /** Demo users to create (with passwords) — used for in-dev seeding. */
  users?: Array<{ username: string; password: string; email?: string }>;
  /** AWS account id — required when `tags` is set so we can build the
   *  UserPool ARN for `TagResourceCommand` on the re-run path. */
  accountId?: string;
  /** Tags to apply at create time AND re-apply on existing pools. */
  tags?: Array<{ Key: string; Value: string }>;
}

export class CognitoAdmin {
  private client: CognitoIdentityProviderClient;

  constructor(cfg: CognitoAuthConfig) {
    this.client = new CognitoIdentityProviderClient({
      region: cfg.region,
      credentials: async () => cfg.credentials(),
    });
  }

  async provisionUserPool(spec: CognitoUserPoolSpec): Promise<{
    poolId: string;
    issuerUrl: string;
    spaClientId: string;
  }> {
    const pool = await this.ensureUserPool(spec.slug, {
      tags: spec.tags,
      accountId: spec.accountId,
    });
    const spaClientId = await this.ensureAppClient(pool.poolId, `${spec.slug}-spa`, {
      callbackUrls: spec.hosts.map((h) => `${h.replace(/\/$/, "")}/`),
      logoutUrls: spec.hosts.map((h) => `${h.replace(/\/$/, "")}/`),
      allowedOAuthFlows: ["code"],
      allowedOAuthScopes: ["openid", "profile", "email"],
    });
    for (const user of spec.users ?? []) {
      await this.ensureUser(pool.poolId, user);
    }
    return {
      poolId: pool.poolId,
      issuerUrl: pool.issuerUrl,
      spaClientId,
    };
  }

  /** Idempotent: returns the existing pool if one with this name exists.
   *  Cognito pool names must be unique within a region for the same
   *  account; we use `{slug}` directly. */
  async ensureUserPool(
    name: string,
    opts: {
      tags?: Array<{ Key: string; Value: string }>;
      /** Required when `tags` is set so we can build the userpool ARN. */
      accountId?: string;
    } = {},
  ): Promise<{ poolId: string; issuerUrl: string }> {
    const existing = await this.findUserPoolByName(name);
    if (existing) {
      log(`user pool '${name}' already exists — reusing (${existing.poolId})`);
      if (opts.tags?.length) {
        if (!opts.accountId) {
          throw new Error("ensureUserPool: tags supplied without accountId");
        }
        const region = await this.region();
        const arn = `arn:aws:cognito-idp:${region}:${opts.accountId}:userpool/${existing.poolId}`;
        await this.client.send(
          new TagResourceCommand({
            ResourceArn: arn,
            Tags: Object.fromEntries(opts.tags.map((t) => [t.Key, t.Value])),
          }),
        );
      }
      return existing;
    }
    log(`creating user pool '${name}'`);
    const userPoolTags = opts.tags?.length
      ? Object.fromEntries(opts.tags.map((t) => [t.Key, t.Value]))
      : undefined;
    const res = await this.client.send(
      new CreateUserPoolCommand({
        PoolName: name,
        // EXPLICIT tier: pools were silently landing on whatever AWS defaults
        // new pools to (Essentials since late 2024). Passwordless (EMAIL_OTP
        // as a first factor) requires Essentials, so pin it — a future AWS
        // default change must not reposition the fleet.
        UserPoolTier: "ESSENTIALS",
        // Lifespan parity with Keycloak's previous accessTokenLifespan
        // (1h) and ssoSessionMaxLifespan (24h). Cognito caps refresh
        // lifetime at 10 years; 30 days matches Keycloak's defaults.
        Policies: {
          PasswordPolicy: {
            MinimumLength: 6,
            RequireUppercase: false,
            RequireLowercase: false,
            RequireNumbers: false,
            RequireSymbols: false,
          },
          // PASSWORDLESS-FIRST: email OTP is a first-class sign-in factor.
          // PASSWORD stays enabled — the e2e harness and anyone who insists
          // on a password keep working; the CLI/UX just never surfaces one.
          SignInPolicy: {
            AllowedFirstAuthFactors: ["PASSWORD", "EMAIL_OTP"],
          },
        },
        AutoVerifiedAttributes: ["email"],
        UsernameAttributes: ["email"],
        AdminCreateUserConfig: {
          AllowAdminCreateUserOnly: true,
        },
        Schema: [
          {
            Name: "email",
            AttributeDataType: "String",
            Required: true,
            Mutable: true,
          },
        ],
        UserPoolTags: userPoolTags,
      }),
    );
    const poolId = res.UserPool?.Id;
    if (!poolId) throw new Error("Cognito CreateUserPool returned no Id");
    const region = await this.region();
    return { poolId, issuerUrl: `https://cognito-idp.${region}.amazonaws.com/${poolId}` };
  }

  async findUserPoolByName(
    name: string,
  ): Promise<{ poolId: string; issuerUrl: string } | null> {
    let nextToken: string | undefined;
    do {
      const res = await this.client.send(
        new ListUserPoolsCommand({ MaxResults: 60, NextToken: nextToken }),
      );
      const found = (res.UserPools ?? []).find((p) => p.Name === name);
      if (found?.Id) {
        const region = await this.region();
        return {
          poolId: found.Id,
          issuerUrl: `https://cognito-idp.${region}.amazonaws.com/${found.Id}`,
        };
      }
      nextToken = res.NextToken;
    } while (nextToken);
    return null;
  }

  async ensureAppClient(
    poolId: string,
    clientName: string,
    opts: {
      callbackUrls: string[];
      logoutUrls: string[];
      allowedOAuthFlows: Array<"code" | "implicit" | "client_credentials">;
      allowedOAuthScopes: string[];
    },
  ): Promise<string> {
    let nextToken: string | undefined;
    do {
      const res = await this.client.send(
        new ListUserPoolClientsCommand({
          UserPoolId: poolId,
          MaxResults: 60,
          NextToken: nextToken,
        }),
      );
      const existing = (res.UserPoolClients ?? []).find((c) => c.ClientName === clientName);
      if (existing?.ClientId) {
        log(`app client '${clientName}' exists — updating`);
        await this.client.send(
          new UpdateUserPoolClientCommand({
            UserPoolId: poolId,
            ClientId: existing.ClientId,
            ClientName: clientName,
            CallbackURLs: opts.callbackUrls,
            LogoutURLs: opts.logoutUrls,
            AllowedOAuthFlows: opts.allowedOAuthFlows,
            AllowedOAuthScopes: opts.allowedOAuthScopes,
            AllowedOAuthFlowsUserPoolClient: true,
            SupportedIdentityProviders: ["COGNITO"],
            ExplicitAuthFlows: [
              "ALLOW_USER_PASSWORD_AUTH",
              "ALLOW_USER_AUTH", // choice-based sign-in (EMAIL_OTP passwordless)
              "ALLOW_REFRESH_TOKEN_AUTH",
            ],
          }),
        );
        return existing.ClientId;
      }
      nextToken = res.NextToken;
    } while (nextToken);

    log(`creating app client '${clientName}'`);
    const created = await this.client.send(
      new CreateUserPoolClientCommand({
        UserPoolId: poolId,
        ClientName: clientName,
        // Public client (PKCE flow). No client secret for browser code.
        GenerateSecret: false,
        CallbackURLs: opts.callbackUrls,
        LogoutURLs: opts.logoutUrls,
        AllowedOAuthFlows: opts.allowedOAuthFlows,
        AllowedOAuthScopes: opts.allowedOAuthScopes,
        AllowedOAuthFlowsUserPoolClient: true,
        SupportedIdentityProviders: ["COGNITO"],
        ExplicitAuthFlows: [
          "ALLOW_USER_PASSWORD_AUTH",
          "ALLOW_USER_AUTH", // choice-based sign-in (EMAIL_OTP passwordless)
          "ALLOW_REFRESH_TOKEN_AUTH",
        ],
      }),
    );
    const id = created.UserPoolClient?.ClientId;
    if (!id) throw new Error("Cognito CreateUserPoolClient returned no ClientId");
    return id;
  }

  /** Create-or-update a user. Cognito doesn't have a single idempotent
   *  upsert — we attempt create, then on collision set the password. */
  async ensureUser(
    poolId: string,
    user: { username: string; password: string; email?: string },
  ): Promise<void> {
    try {
      await this.client.send(
        new AdminCreateUserCommand({
          UserPoolId: poolId,
          Username: user.username,
          TemporaryPassword: user.password,
          MessageAction: "SUPPRESS",
          UserAttributes: [
            { Name: "email", Value: user.email ?? `${user.username}@example.test` },
            { Name: "email_verified", Value: "true" },
          ],
        }),
      );
      log(`created user '${user.username}'`);
    } catch (e) {
      const code = (e as { name?: string })?.name;
      if (code !== "UsernameExistsException") throw e;
      log(`user '${user.username}' exists — refreshing password`);
    }
    // Set to permanent so it doesn't require change on first login.
    await this.client.send(
      new AdminSetUserPasswordCommand({
        UserPoolId: poolId,
        Username: user.username,
        Password: user.password,
        Permanent: true,
      }),
    );
  }

  async ensureGroup(poolId: string, name: string): Promise<void> {
    try {
      await this.client.send(new GetGroupCommand({ UserPoolId: poolId, GroupName: name }));
      return;
    } catch (e) {
      const code = (e as { name?: string })?.name;
      if (code !== "ResourceNotFoundException") throw e;
    }
    log(`creating group '${name}' in pool ${poolId}`);
    await this.client.send(
      new CreateGroupCommand({ UserPoolId: poolId, GroupName: name }),
    );
  }

  private cachedRegion: string | null = null;
  private async region(): Promise<string> {
    if (this.cachedRegion) return this.cachedRegion;
    // CognitoIdentityProviderClient.config.region can be a function or a
    // value; resolve uniformly.
    const r = this.client.config.region;
    const resolved = typeof r === "function" ? await r() : r;
    if (!resolved) throw new Error("CognitoAdmin: region not configured");
    this.cachedRegion = resolved;
    return resolved;
  }
}

function log(msg: string): void {
  console.log(`[cognito] ${msg}`);
}
